import React, { useState } from "react";

// Each screen remembers its own tab (court statistics and Beauty statistics have different sections).
const DEFAULT_STORAGE_KEY = "zocoStatsTab";

// Storage can be missing or throw (private windows, blocked site data): the tab is then simply not
// remembered.
const readSavedTab = (storageKey, ids) => {
  try {
    const saved = window.localStorage.getItem(storageKey);
    return ids.includes(saved) ? saved : null;
  } catch (_) {
    return null;
  }
};
const saveTab = (storageKey, id) => {
  try {
    window.localStorage.setItem(storageKey, id);
  } catch (_) {
    /* not remembered */
  }
};

/**
 * Section tabs following the ARIA tabs pattern: roving tabindex, arrow keys / Home / End move
 * between tabs (wrapping) and select the focused one. Only the selected panel renders its content;
 * the other panels stay in the DOM, hidden, so `aria-controls` always points at a real element.
 * The last selected tab is remembered in localStorage under `storageKey`. The list of tabs may
 * change while mounted (a section that only some viewers get): a selected tab that is no longer in
 * the list falls back to the first one.
 *
 * @param {{tabs: Array<{id: string, label: string}>, label: string, className?: string,
 *   busy?: boolean, storageKey?: string, children: (id: string) => React.ReactNode}} props
 *   `children` renders the content of a tab; `className` styles the panels' wrapper.
 */
export default function StatsTabs({ tabs, label, className = "", busy = false, storageKey = DEFAULT_STORAGE_KEY, children }) {
  const ids = tabs.map((t) => t.id);
  const [saved, setSelected] = useState(() => readSavedTab(storageKey, ids) || ids[0]);
  const selected = ids.includes(saved) ? saved : ids[0];

  const select = (id, focus = false) => {
    setSelected(id);
    saveTab(storageKey, id);
    if (focus) document.getElementById(`stats-tab-${id}`)?.focus();
  };

  const onKeyDown = (event, index) => {
    let target = null;
    if (event.key === "ArrowRight") target = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") target = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") target = 0;
    else if (event.key === "End") target = tabs.length - 1;
    if (target === null) return;
    event.preventDefault();
    select(tabs[target].id, true);
  };

  return (
    <>
      <div className="stats-tabs" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`stats-tab-${tab.id}`}
            className="stats-tab"
            aria-selected={selected === tab.id}
            aria-controls={`stats-panel-${tab.id}`}
            tabIndex={selected === tab.id ? 0 : -1}
            onClick={() => select(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={className} aria-busy={busy}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`stats-panel-${tab.id}`}
            className="stats-tab-panel"
            aria-labelledby={`stats-tab-${tab.id}`}
            hidden={selected !== tab.id}
          >
            {selected === tab.id ? children(tab.id) : null}
          </div>
        ))}
      </div>
    </>
  );
}
